import { saveAs } from "foundation/js/file-download";
import { GTMService } from '../../services/gtm.service.js';
import { ChatConversationService } from '../../services/chat-conversation.service';
import { MessageFormattingService } from '../../services/message-formatting.service.js';

export const CHAT_PANEL_USED_ACTION_IDS_SESSION_STORAGE_KEY = "chatPanelUsedActionIds";

function isConversationSessionExpired(createdDate) {
  if (!createdDate) return false;

  const currentDate = new Date();

  if (typeof createdDate === "string") {
    createdDate = new Date(createdDate);
  }

  const timeDifference = currentDate.getTime() - createdDate.getTime();
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
  return daysDifference >= 1;
}

function reset(commit, dispatch, getters) {
  localStorage.removeItem("chatPanelConversation");
  dispatch("stopPolling");
  if (getters["isConversationOngoing"]) {
    dispatch("endConversation");
  }

  commit("reset_chat_client_state");
}

function findLastBotResponseId(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].source === "bot") {
      return messages[i].id;
    }
  }
  return null;
}

function createMessageObject(textContent, id) {
  return {
    id: id,
    dateCreated: null,
    language: null,
    source: "client",
    elements: [
      {
        type: "text",
        payload: {
          html: null,
          text: textContent,
          links: null,
        },
      },
    ],
  };
}

function createStatusMessageObject(statusText) {
  return {
    ...createMessageObject("", "status_message"),
    source: "system",
    elements: [
      {
        type: "statusMessage",
        payload: {
          statusMessage: statusText,
        },
      },
    ],
  };
}

function createClientMessageId(state) {
  return state.latestResponseId
    ? (parseInt(state.latestResponseId) + 1).toString()
    : undefined;
}

export default {
  namespaced: true,
  state: {
    broadcaster: null,
    createdDate: undefined,
    conversation: undefined,
    messages: [],
    chatWindowOpen: false,
    showFeedback: false,
    connectionError: false,
    labels: undefined,
    feedbackActionId: undefined,
    messageFeedback: [],
    latestResponseId: undefined,
    maxCharacters: undefined,
    isAwaitingResponse: false,
    feedbackStep: 'ChatFeedbackInput',
    feedbackPayload: undefined,
    expiredMessageAdded: false,
    previousWasHumanAgent: false,
    triggerActionId: undefined,
  },
  mutations: {
    set_is_awaiting_response(state, value) {
      state.isAwaitingResponse = value;
    },
    set_conversation(state, value) {
      state.conversation = value;
    },
    set_created_date(state, value) {
      state.createdDate = value;
    },
    set_broadcaster(state, value) {
      state.broadcaster = value;
    },
    set_feedback_step(state, value) {
      state.feedbackStep = value
    },
    set_feedback_payload(state, value) {
      state.feedbackPayload = value
    },
    append_messages(state, messages) {
      if (!messages || !messages.length) {
        return true
      }
      // Filter system messages since these are not supplied by Boost api and should not be filteren on latestResponseId
      const newMessagesList = messages.filter(message => message.source === "system" || (state.latestResponseId ? parseInt(message.id) > parseInt(state.latestResponseId) : true));
      let index = 0;
      while (index < newMessagesList.length) {
        const message = newMessagesList[index];

        message.id = message.id ? message.id : new Date().getTime();
        if (message.elements && message.elements.length) {
          message.elements = message.elements.map((element, index) => element.id ? element : { ...element, id: new Date().getTime() + index });
        }

        if (message.source === "client") {
          break
        }
        if (message.isHumanAgent && !state.previousWasHumanAgent) {
          const newMessage = createStatusMessageObject(state.labels["customerServiceJoined"]);
          newMessagesList.splice(index, 0, newMessage);
          state.previousWasHumanAgent = true;
          index++;
        } if (!message.isHumanAgent && state.previousWasHumanAgent) {
          const newMessage = createStatusMessageObject(state.labels["virtualAgentJoined"]);
          newMessagesList.splice(index, 0, newMessage);
          state.previousWasHumanAgent = false;
          index++;
        }
        index++;
      }

      if (state.messages && state.messages.length) {
        if (newMessagesList.length) {
          state.messages = [...state.messages, ...MessageFormattingService.processResponses(newMessagesList, { setLastMessageInGroup: true, setAiGenerated: true, setIsReady: true })];
        }
      } else {
        state.messages = [...MessageFormattingService.processResponses(newMessagesList, { setLastMessageInGroup: true, setAiGenerated: true, setIsReady: true })];
      }
    },
    set_messages(state, messages) {
      state.messages = [...messages];
    },
    set_chat_window_open(state, value) {
      const localStorageString = localStorage.getItem("chatPanelConversation");
      if (localStorageString) {
        const localStorageObject = JSON.parse(localStorageString);
        const createdDate = new Date();
        this.commit('chatPanel/set_conversation', localStorageObject.conversation);
        this.commit('chatPanel/set_created_date', createdDate);
        localStorageObject.windowOpen = value;
        localStorageObject.createdDate = createdDate;
        localStorage.setItem("chatPanelConversation", JSON.stringify(localStorageObject));
      }
      state.chatWindowOpen = value;
    },
    set_labels(state, payload) {
      state.labels = payload;
    },
    set_feedback_action_id(state, value) {
      state.feedbackActionId = value;
    },
    set_show_feedback(state, value) {
      state.showFeedback = value;
    },
    set_message_feedback(state, payload) {
      const { id, value } = payload;
      const index = state.messageFeedback.findIndex((item) => item.id === id);

      if (index !== -1) {
        state.messageFeedback[index].value = value;
      } else {
        state.messageFeedback.push({ id, value });
      }
    },
    set_connection_error(state, value) {
      state.connectionError = value;
    },
    set_latest_response_id(state, value) {
      state.latestResponseId = value;
    },
    setPollingInterval(state, intervalId) {
      state.pollingInterval = intervalId;
    },
    clearPollingInterval(state) {
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
        state.pollingInterval = null;
      }
    },
    expired_message_added(state, value) {
      state.expiredMessageAdded = value;
    },
    set_feedback_api_message(state, value) {
      state.feedbackApiMessage = value
    },
    set_floating_trigger_action_id(state, triggerActionId) {
      state.triggerActionId = triggerActionId;
    },
    reset_chat_client_state(state) {
      state.conversation = undefined;
      state.messages = [];
      state.showFeedback = false;
      state.connectionError = false;
      state.chatWindowOpen = false;
      state.messageFeedback = [];
      state.latestResponseId = undefined;
      state.maxCharacters = undefined;
      state.isAwaitingResponse = false;
      state.feedbackApiMessage = undefined;
      state.feedbackStep = 'ChatFeedbackInput';
      state.triggerActionId = undefined;
      localStorage.removeItem("chatPanelConversation");
      localStorage.removeItem(CHAT_PANEL_USED_ACTION_IDS_SESSION_STORAGE_KEY);
    }
  },
  actions: {
    initializeChatPanel({ commit, dispatch, getters }) {
      const localStorageChatPanelConversation = JSON.parse(localStorage.getItem('chatPanelConversation'));

      if (localStorageChatPanelConversation && isConversationSessionExpired(localStorageChatPanelConversation.createdDate)) {
        localStorage.removeItem('chatPanelConversation');
      }

      const bc = new BroadcastChannel("nyk-chat-conversation-id");
      commit('set_broadcaster', bc);

      bc.onmessage = (e) => {
        if (e.data === "deleteChatConversation") {
          // Chat session has been deleted in another tab
          reset(commit, dispatch, getters)
        }
        else if (e.data.updatedConversationalData) {
          const conversationalData = JSON.parse(e.data.updatedConversationalData);
          dispatch("handleConversationResponse", { data: conversationalData, action: 'set' });

          if (conversationalData.windowOpen) {
            commit("set_chat_window_open", true);
          }
        }
      };
    },
    resumeOrStartConversation({ dispatch }) {
      const localStorageChatPanelConversation = JSON.parse(localStorage.getItem('chatPanelConversation'));

      if (!localStorageChatPanelConversation) {
        dispatch("startConversation");
        return true
      }
      else if (localStorageChatPanelConversation?.conversation?.id) {
        dispatch("resumeConversation")
      }
    },
    openChatAndHandleActionIdFromQueryParam({ state, commit, getters, dispatch }) {
      const urlParams = new URLSearchParams(window.location.search);
      const requestedActionId = urlParams.get('chatPanel');

      if (requestedActionId) {
        const usedChatActionIds = JSON.parse(localStorage.getItem(CHAT_PANEL_USED_ACTION_IDS_SESSION_STORAGE_KEY) || '[]');

        if (requestedActionId !== 'open') {
          commit('set_floating_trigger_action_id', requestedActionId);
        }

        commit("set_chat_window_open", true);

        if (getters["isConversationOngoing"]) {
          dispatch("resumeConversation")
        }

        // Present the action id, only if it has not been requested before.
        if (requestedActionId !== 'open' && !usedChatActionIds.includes(requestedActionId)) {
          if (getters["isConversationOngoing"]) {
            dispatch("sendActionId", { actionId: requestedActionId, isFloatingTrigger: true });
          }

          usedChatActionIds.push(requestedActionId);
          localStorage.setItem(CHAT_PANEL_USED_ACTION_IDS_SESSION_STORAGE_KEY, JSON.stringify(usedChatActionIds));
        }
      }
    },
    openChatAndHandleActionId({ state, commit, getters, dispatch }, actionId) {
      commit("set_floating_trigger_action_id", actionId);

      if (!state.chatWindowOpen) {
        commit("set_chat_window_open", true);
        dispatch("registerChatOpenedEvent");
      }
      if (getters["isConversationOngoing"]) {
        dispatch("sendActionId", { actionId, isFloatingTrigger: true });
      }
    },
    registerChatOpenedEvent() {
      GTMService.pushToDataLayer([
        {
          event: 'track-event.chat_click_open',
          eventCategory: 'chat',
          eventAction: 'click',
          eventLabel: 'open chat - floating',
          eventValue: '0',
          userType: 'privat',
          context: 'floating'
        }
      ])
    },
    async startConversation({ state, commit, dispatch }) {
      try {
        commit("set_is_awaiting_response", true);

        const json = await ChatConversationService.startConversationWithActionId(
          'chatPanelConversation',
          state.triggerActionId
        );

        commit("set_connection_error", false);
        dispatch("handleConversationData", { json, action: 'set' });
        return true;
      } catch (err) {
        console.error("Error starting conversation:", err);
        commit("set_connection_error", true);
        throw new Error("Unable to start conversation: " + err);
      } finally {
        commit("set_is_awaiting_response", false);
      }
    },
    async resumeConversation({ commit, dispatch, state }) {
      const localStorageChatPanelConversation = localStorage.getItem('chatPanelConversation');
      if (!localStorageChatPanelConversation) {
        console.error("No conversation to resume");
        return false
      }
      const parsedLocalStorageConversation = JSON.parse(localStorageChatPanelConversation);
      const id = parsedLocalStorageConversation?.conversation?.id;

      try {
        commit("set_is_awaiting_response", true);

        const json = await ChatConversationService.resumeConversation(id);

        commit("set_connection_error", false);
        dispatch("handleConversationData", { json });
        return true;
      } catch (err) {
        console.error("Error resuming conversation:", err);
        commit("set_connection_error", true);
        throw new Error("Unable to resume conversation: " + err);
      } finally {
        commit("set_is_awaiting_response", false);
      }
    },
    handleConversationData({ commit, dispatch, getters, state }, { json, action = 'append' }) {
      dispatch("setConversationState", json.conversation);

      commit("set_feedback_api_message", json.response || json.responses);

      if (getters["isConversationExpired"] && !state.expiredMessageAdded) {
        const localStorageChatPanelConversation = localStorage.getItem('chatPanelConversation');
        let localStorageMessages = [];
        if (localStorageChatPanelConversation) {
          const parsedLocalStorageConversation = JSON.parse(localStorageChatPanelConversation);
          localStorageMessages = parsedLocalStorageConversation.response ? parsedLocalStorageConversation.response : parsedLocalStorageConversation.responses
        }
        const expiredMessage = [...localStorageMessages, ...[createStatusMessageObject(state.labels["chatExpiredStatusMessage"])]]
        const conversationWithExpiredMessage = {
          conversation: json.conversation,
          ...expiredMessage.length ? { responses: expiredMessage } : { response: expiredMessage }
        }
        dispatch("handleConversationResponse", { data: conversationWithExpiredMessage, action })
        commit("expired_message_added", true)
        return false
      }
      else {
        dispatch("handleConversationResponse", { data: json, action });
        const conversationalData = JSON.stringify({ conversation: state.conversation, createdDate: new Date(), responses: state.messages, windowOpen: state.chatWindowOpen });
        localStorage.setItem("chatPanelConversation", conversationalData);
        state.broadcaster.postMessage({ updatedConversationalData: conversationalData });
      }
    },
    handleConversationResponse({ commit, dispatch, state }, { data, action }) {
      if (data.response) {
        if (action === 'append') {
          commit("append_messages", [data.response]);
        }
        else {
          commit("set_messages", [data.response]);
        }
      } else if (data.responses && data.responses.length) {
        if (action === 'append') {
          commit("append_messages", data.responses);
        }
        else {
          commit("set_messages", data.responses);
        }
      }
      const lastestBotResponse = findLastBotResponseId(state.messages);
      if (lastestBotResponse !== null) {
        commit("set_latest_response_id", lastestBotResponse);
      }
      if (data.conversation.state.poll) {
        dispatch("startPolling");
      } else {
        dispatch("stopPolling");
      }
    },
    async endConversation({ state, dispatch }) {
      try {
        await ChatConversationService.endConversation(state.conversation.id);
        dispatch('resetChat');
        return true;
      } catch (err) {
        throw new Error("Unable to end conversation.: " + err);
      }
    },
    async sendActionId({ commit, state, dispatch }, { actionId, isFloatingTrigger = false } = {}) {
      try {
        commit("set_is_awaiting_response", true);
        const json = await ChatConversationService.sendActionId(
          state.conversation.id,
          actionId,
          isFloatingTrigger
        );

        dispatch("handleConversationData", { json });
        return true;
      } catch (err) {
        throw new Error("Unable to send action link: " + err);
      } finally {
        commit("set_is_awaiting_response", false);
      }
    },
    async sendExternalLink({ state }, payload = {}) {
      try {
        await ChatConversationService.sendExternalLink(state.conversation.id, payload);
      } catch (err) {
        throw new Error("Unable to send external link: " + err);
      }
    },
    addClientMessage({ commit, state }, payload) {
      const clientMessageId = createClientMessageId(state);
      commit("append_messages", [createMessageObject(payload, clientMessageId)]);
    },
    async sendMessage({ state, commit, dispatch }, payload) {
      try {
        dispatch("addClientMessage", payload)
        commit("set_is_awaiting_response", true);

        const json = await ChatConversationService.sendMessage(
          state.conversation.id,
          payload
        );

        dispatch("handleConversationData", { json });
        return true;
      } catch (err) {
        throw new Error("Unable to send message: " + err);
      } finally {
        commit("set_is_awaiting_response", false);
      }
    },
    async sendMessageFeedback({ state, commit }, payload) {
      const { feedback, id } = payload;

      try {
        await ChatConversationService.sendMessageFeedback(state.conversation.id, feedback, id);

        commit("set_message_feedback", {
          id: payload.id,
          value: payload.feedback,
        });
        return true;
      } catch (err) {
        throw new Error("Unable to send feedback: " + err);
      }
    },
    async sendConversationFeedback({ state, commit }, payload) {
      try {
        const json = await ChatConversationService.sendConversationFeedback(
          state.conversation.id,
          payload.feedback,
          state.feedbackActionId
        );

        commit("set_feedback_payload", payload.feedback.feedback)
        commit("set_feedback_api_message", json)
        localStorage.removeItem("chatPanelConversation");
        return true;
      } catch (err) {
        throw new Error("Unable to send feedback: " + err);
      }
    },
    async deleteMessageHistory({ state, dispatch }) {
      try {
        await ChatConversationService.deleteMessageHistory(state.conversation.id);
        dispatch("resetChat");
        return true;
      } catch (err) {
        throw new Error("Unable to delete message history: " + err);
      }
    },
    async downloadConversation({ state }) {
      try {
        const blob = await ChatConversationService.downloadConversation(state.conversation.id);
        saveAs(blob, "Conversation.txt");
        return true;
      } catch (error) {
        throw new Error("Unable to download message history: " + error);
      }
    },
    async startPolling({ commit, state, dispatch }) {
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
      }

      const intervalId = setInterval(async () => {
        try {
          const json = await ChatConversationService.pollConversationStatus(
            state.conversation.id,
            state.latestResponseId
          );

          dispatch("handleConversationData", { json });
          commit("set_connection_error", false);
        } catch (error) {
          commit("set_connection_error", true);
          throw new Error(error);
        }
      }, 3000);
      commit("setPollingInterval", intervalId);
    },
    setConversationState({ commit }, payload) {
      commit("set_conversation", payload);
      commit('set_created_date', new Date());
    },
    stopPolling({ commit }) {
      commit("clearPollingInterval");
    },
    minimizeChatWindow({ commit }) {
      commit("set_chat_window_open", false);
    },
    showFeedback({ commit }, payload) {
      commit("set_show_feedback", payload);
    },
    resetChat({ state, commit, dispatch, getters }) {
      reset(commit, dispatch, getters)
      state.broadcaster.postMessage("deleteChatConversation");
    }
  },
  getters: {
    isConversationActive(state) {
      return state.conversation?.id;
    },
    isConversationOngoing(state) {
      return state.conversation?.id && !state.conversation?.state?.expired && !isConversationSessionExpired(state.createdDate);
    },
    isConversationExpired(state) {
      return state.conversation?.state?.expired || isConversationSessionExpired(state.createdDate);
    },
    getFeedbackApiMessage(state) {
      if (
        state.feedbackApiMessage?.elements &&
        state.feedbackApiMessage.elements.length
      ) {
        return state.feedbackApiMessage?.elements[0].payload.html;
      }
      return ""
    },
  }
};